/// <reference types="office-js" />
/**
 * Office.js initialization and core utilities
 */
import { setTag } from '@sentry/react';
import { marked } from 'marked';

import type { EmailItem, OfficeInitResult } from './types';

/**
 * Stamps which Outlook build this session runs in on every Sentry event. The
 * auth surfaces differ per host (webview, dialog behavior, storage
 * partitioning), so "is this failing on new Outlook?" must be answerable by a
 * tag search — during the federated-COOP incident it wasn't.
 */
function tagOfficeEnvironment(info: { host: Office.HostType; platform: Office.PlatformType }) {
  setTag('officePlatform', Office.PlatformType[info.platform]);
  setTag('officeHost', Office.HostType[info.host]);
  try {
    // Distinguishes new Outlook on Windows from Outlook on the web — both
    // report platform OfficeOnline, but hostName differs.
    const diagnostics = Office.context?.mailbox?.diagnostics;
    if (diagnostics?.hostName) {
      setTag('outlookHostName', diagnostics.hostName);
    }
  } catch {
    // Diagnostics are unavailable outside a mailbox context — the platform
    // tags above still stand.
  }
}

/**
 * Detects if text contains HTML tags
 */
const isHtmlContent = (text: string): boolean => {
  return /<[^>]+>/.test(text);
};

/**
 * Converts Markdown text to HTML using marked library
 */
function convertMarkdownToHtml(text: string): string {
  try {
    // Configure marked to handle line breaks like GitHub flavored markdown
    // usage of 'await' is avoided by using synchronous parsing if available or handling the promise
    // marked.parse can be synchronous if async option is false (default in newer versions depends on extensions)
    // However, simplest usage in TS usually works.

    // Using synchronous parse (marked.parse returns string by default without async extensions)
    const html = marked.parse(text, { async: false, breaks: true }) as string;
    return html;
  } catch (error) {
    console.error('Markdown conversion failed, falling back to simple text conversion', error);
    return convertPlainTextToHtml(text);
  }
}

/**
 * Initialize Office.js and return host information
 * Uses Office.onReady() which is the modern initialization pattern
 */
export async function initializeOffice(): Promise<OfficeInitResult> {
  return new Promise((resolve, reject) => {
    // Check if Office is available
    if (typeof Office === 'undefined') {
      reject(new Error('Office.js is not loaded'));
      return;
    }

    // Use Office.onReady() - modern pattern that returns a Promise
    Office.onReady((info: { host: Office.HostType; platform: Office.PlatformType }) => {
      if (info.host && info.platform) {
        console.log('Office.js initialized:', {
          host: Office.HostType[info.host],
          platform: Office.PlatformType[info.platform],
        });
        tagOfficeEnvironment(info);
        resolve({
          host: info.host,
          platform: info.platform,
        });
      } else {
        reject(new Error('Office.js initialization failed'));
      }
    });
  });
}

/**
 * Converts plain text to HTML with basic formatting preservation
 */
function convertPlainTextToHtml(text: string): string {
  return text
    .split('\n')
    .map((line) => {
      // Escape HTML special characters to prevent interpretation as HTML
      const escaped = line
        .replaceAll('&', '&amp;') // Must be first!
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');

      // Preserve ONLY leading spaces (indentation) with &nbsp;
      // This allows text to wrap naturally while maintaining indentation
      const leadingSpacesRegex = /^(\s+)/;
      const leadingSpaces = leadingSpacesRegex.exec(escaped)?.[0] ?? '';
      const restOfLine = escaped.slice(leadingSpaces.length);

      // Convert leading spaces to &nbsp; to preserve indentation
      const preservedIndent = leadingSpaces.replaceAll(' ', '&nbsp;');

      return preservedIndent + restOfLine;
    })
    .join('<br>');
}

/**
 * Gets the body content of the currently open compose window
 * @param coercionType - The format to retrieve (Html or Text), defaults to Html
 * @returns The body content as a string
 */
export async function getComposeBody(
  coercionType: Office.CoercionType = Office.CoercionType.Html,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!Office.context.mailbox?.item) {
      reject(new Error('No compose window is open'));
      return;
    }

    Office.context.mailbox.item.body.getAsync(coercionType, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(asyncResult.error.message));
      } else {
        resolve(asyncResult.value);
      }
    });
  });
}

/**
 * Saves the current draft
 * useful to ensure we have the latest content before reading
 */
export async function saveDraft(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!Office.context.mailbox?.item) {
      reject(new Error('No email is open'));
      return;
    }

    Office.context.mailbox.item.saveAsync((asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(asyncResult.error.message));
      } else {
        resolve();
      }
    });
  });
}

interface ComposeWindowOptions {
  bcc?: string;
  body: string;
  cc?: string;
  isHtml?: boolean;
  subject?: string;
  to?: string;
}

/**
 * Probe the current compose body's format. Office.js has no API to flip a
 * compose from plain-text to HTML format — that's a property of the message
 * fixed at compose creation. Callers use this to decide whether they can
 * setAsync HTML in place or need to fall back to a structured plain-text
 * conversion.
 */
function getComposeBodyType(): Promise<Office.CoercionType> {
  return new Promise((resolve, reject) => {
    const item = Office.context?.mailbox?.item as Office.MessageCompose | undefined;
    if (!item?.body?.getTypeAsync) {
      reject(new Error('Compose body APIs not available'));
      return;
    }
    item.body.getTypeAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(result.error.message));
      } else {
        resolve(result.value);
      }
    });
  });
}

function setComposeBody(value: string, coercionType: Office.CoercionType): Promise<void> {
  return new Promise((resolve, reject) => {
    const item = Office.context?.mailbox?.item as Office.MessageCompose | undefined;
    if (!item?.body?.setAsync) {
      reject(new Error('Compose body APIs not available'));
      return;
    }
    item.body.setAsync(value, { coercionType }, (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        reject(new Error(result.error.message));
      } else {
        resolve();
      }
    });
  });
}

function walkHtmlNodeToText(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return '';
  }
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  const inner = Array.from(el.childNodes).map(walkHtmlNodeToText).join('');

  switch (tag) {
    case 'br': {
      return '\n';
    }
    case 'p':
    case 'div':
    case 'h1':
    case 'h2':
    case 'h3':
    case 'h4':
    case 'h5':
    case 'h6': {
      return `${inner}\n\n`;
    }
    case 'ul':
    case 'ol': {
      return `${inner}\n`;
    }
    case 'li': {
      const parent = el.parentElement;
      const trimmed = inner.replaceAll(/\s+/g, ' ').trim();
      if (parent?.tagName.toLowerCase() === 'ol') {
        const idx = Array.from(parent.children).indexOf(el) + 1;
        return `${idx}. ${trimmed}\n`;
      }
      return `• ${trimmed}\n`;
    }
    case 'blockquote': {
      return inner
        .split('\n')
        .map((line) => (line.length > 0 ? `> ${line}` : line))
        .join('\n');
    }
    default: {
      return inner;
    }
  }
}

/**
 * Render the draft HTML as readable plain text — preserving paragraph
 * spacing, bullet markers, and numbered-list indices — so a plain-text
 * compose doesn't end up with either raw markup or one-long-blob output.
 * Used only when the current compose is plain-text format and we have no
 * way to escape to HTML (Office.js exposes no compose-to-HTML conversion;
 * `displayNewMessageForm` is documented as Read-mode only).
 */
function htmlToReadablePlainText(html: string): string {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstChild;
  if (!root) {
    return '';
  }
  return walkHtmlNodeToText(root)
    .replaceAll(/\n{3,}/g, '\n\n')
    .trim();
}

function buildMessageFormOptions(options: ComposeWindowOptions, htmlBody: string) {
  const newMessageOptions: {
    bccRecipients?: Array<string>;
    ccRecipients?: Array<string>;
    htmlBody: string;
    subject?: string;
    toRecipients?: Array<string>;
  } = { htmlBody };

  if (options.subject) {
    newMessageOptions.subject = options.subject;
  }
  if (options.to) {
    newMessageOptions.toRecipients = [options.to];
  }
  if (options.cc) {
    newMessageOptions.ccRecipients = options.cc.split(',').map((email) => email.trim());
  }
  if (options.bcc) {
    newMessageOptions.bccRecipients = options.bcc.split(',').map((email) => email.trim());
  }
  return newMessageOptions;
}

/**
 * Opens a new compose window or updates the current one based on context
 * Automatically detects if in compose mode or read mode.
 *
 * Plain-text compose handling: Office.js can't switch an existing compose
 * from plain-text to HTML — the format is fixed at creation, and
 * `displayNewMessageForm` is documented as Read-mode only so it isn't
 * available to escape to a fresh HTML compose from inside one. When the
 * current compose is plain-text we render the draft as readable plain text
 * (bullets preserved, paragraphs spaced) and set it in place.
 */
export async function openOrUpdateComposeWindow(options: ComposeWindowOptions): Promise<void> {
  if (!Office.context?.mailbox) {
    throw new Error('Outlook API not available');
  }

  // Intelligent detection: If content doesn't have HTML tags, convert it from plain text
  const htmlBody =
    options.isHtml || isHtmlContent(options.body)
      ? options.body
      : convertPlainTextToHtml(options.body);

  // Route into "update current window" whenever the item exposes compose APIs
  // (covers both a brand-new compose and an existing draft being edited).
  // Calling body.setAsync on a read-mode item throws
  // `setAsync is not a function`, so this must be a capability check, not an
  // itemId heuristic.
  const canUpdateCurrentWindow = isInComposeMode() || isReadingDraft();

  if (canUpdateCurrentWindow) {
    const item = Office.context.mailbox.item!;
    const bodyType = await getComposeBodyType();
    const isPlainText = bodyType !== Office.CoercionType.Html;

    if (isPlainText) {
      await setComposeBody(htmlToReadablePlainText(htmlBody), Office.CoercionType.Text);
    } else {
      await setComposeBody(htmlBody, Office.CoercionType.Html);
    }

    const additionalOperations: Array<Promise<void>> = [];

    if (options.subject && item.subject) {
      additionalOperations.push(
        new Promise<void>((resolveSubject, rejectSubject) => {
          item.subject.setAsync(options.subject!, (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
              rejectSubject(new Error(`Failed to set subject: ${asyncResult.error.message}`));
            } else {
              resolveSubject();
            }
          });
        }),
      );
    }

    if (options.to && item.to) {
      additionalOperations.push(
        new Promise<void>((resolveTo, rejectTo) => {
          item.to.setAsync([{ displayName: '', emailAddress: options.to! }], (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
              rejectTo(new Error(`Failed to set recipients: ${asyncResult.error.message}`));
            } else {
              resolveTo();
            }
          });
        }),
      );
    }

    if (options.cc && item.cc) {
      const ccRecipients = options.cc
        .split(',')
        .map((email) => ({ displayName: '', emailAddress: email.trim() }));
      additionalOperations.push(
        new Promise<void>((resolveCc, rejectCc) => {
          item.cc.setAsync(ccRecipients, (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
              rejectCc(new Error(`Failed to set CC recipients: ${asyncResult.error.message}`));
            } else {
              resolveCc();
            }
          });
        }),
      );
    }

    if (options.bcc && item.bcc) {
      const bccRecipients = options.bcc
        .split(',')
        .map((email) => ({ displayName: '', emailAddress: email.trim() }));
      additionalOperations.push(
        new Promise<void>((resolveBcc, rejectBcc) => {
          item.bcc.setAsync(bccRecipients, (asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
              rejectBcc(new Error(`Failed to set BCC recipients: ${asyncResult.error.message}`));
            } else {
              resolveBcc();
            }
          });
        }),
      );
    }

    await Promise.all(additionalOperations);
    return;
  }

  // Read mode — open a new compose window. `displayNewMessageForm` always
  // creates an HTML-format compose.
  if (!Office.context.mailbox.displayNewMessageForm) {
    throw new Error('Unable to open compose window in this Outlook version');
  }
  Office.context.mailbox.displayNewMessageForm(buildMessageFormOptions(options, htmlBody));
}

/**
 * Check if Office.js is initialized
 */
function isOfficeInitialized(): boolean {
  return typeof Office !== 'undefined' && Office.context !== undefined;
}

/**
 * Heuristic for whether an id is already in Graph REST format. REST (v2.0) ids
 * are URL-safe base64 (`-`/`_`); EWS ids are standard base64 and contain `+` or
 * `/`. Used only in the conversion fallback path — a raw EWS id shipped to Graph
 * is rejected with `ErrorInvalidIdMalformed` (see OUTLOOK-SERVER-25), so when we
 * can't convert we pass the id through only if it's already REST-shaped.
 */
export function isLikelyEwsId(id: string): boolean {
  return /[+/]/.test(id);
}

/**
 * Convert an EWS item ID to REST format for use with Microsoft Graph.
 * On desktop/web Outlook, itemId is in EWS format which is not URL-safe and must
 * be converted. On mobile Outlook the ID is already REST, so `convertToRestId`
 * is a no-op / unavailable and we pass it through.
 *
 * Returns null when no usable REST id can be produced. Callers MUST treat null
 * as "no current item" — never fall back to the raw id, which Graph rejects as
 * malformed (the previous raw-id fallback was the root cause of OUTLOOK-SERVER-25).
 * @param rawId - The item ID from Office.js (EWS on desktop/web, REST on mobile)
 * @returns The REST-formatted item ID, or null if none can be produced
 */
function convertToRestId(rawId: string | null | undefined): string | null {
  if (!rawId) {
    return null;
  }

  // Attempt the real conversion first: on desktop/web it correctly handles every
  // EWS id (including the rare ones the format heuristic can't recognise).
  if (isOfficeInitialized()) {
    try {
      return Office.context.mailbox.convertToRestId(rawId, Office.MailboxEnums.RestVersion.v2_0);
    } catch (error) {
      console.error('Failed to convert EWS ID to REST format:', error);
      // fall through to the fallback below
    }
  }

  // Office unavailable or conversion threw (e.g. mobile, where the id is already
  // REST). Pass the id through only if it's already REST-shaped; never ship a
  // raw EWS id to Graph.
  return isLikelyEwsId(rawId) ? null : rawId;
}

/**
 * Convert a REST item ID back to EWS format for use with Office.js methods.
 * This is needed when we get an ID from the backend (Graph REST format)
 * and need to use it with Office.js methods like displayMessageForm.
 * @param restId - The REST-formatted item ID
 * @returns The EWS-formatted item ID, or null if conversion fails
 */
function convertToEwsId(restId: string | null | undefined): string | null {
  if (!restId) {
    return null;
  }
  if (!isOfficeInitialized()) {
    return restId;
  }

  try {
    // convertToEwsId returns an EWS-formatted ID suitable for Office.js
    const ewsId = Office.context.mailbox.convertToEwsId(
      restId,
      Office.MailboxEnums.RestVersion.v2_0,
    );
    return ewsId;
  } catch (error) {
    console.error('Failed to convert REST ID to EWS format:', error);
    // Fallback to original ID if conversion fails
    return restId;
  }
}

/**
 * Current Outlook context summary
 */
export interface OutlookContext {
  conversationId: string | null;
  itemId: string | null;
  subject: string | null;
  type: 'email' | 'none';
}

/**
 * Get the current Outlook context (what is opened)
 * Note: itemId is converted from EWS to REST format for Graph API compatibility
 * For some reason item.itemId does not come when the addin is not pinned in the outlook app
 */
export function getOutlookContext(): OutlookContext {
  if (!isOfficeInitialized()) {
    return { conversationId: null, itemId: null, subject: null, type: 'none' };
  }

  const item = Office.context.mailbox.item;
  if (!item) {
    return { conversationId: null, itemId: null, subject: null, type: 'none' };
  }

  // Convert EWS itemId to REST format for Graph API compatibility
  const restItemId = convertToRestId(item.itemId);

  const context: OutlookContext = {
    conversationId: null,
    itemId: restItemId,
    subject: item.subject || null,
    type: 'none',
  };

  if (item.itemType === Office.MailboxEnums.ItemType.Message) {
    context.type = 'email';
    // conversationId is available on Message objects
    const message = item as Office.MessageRead | Office.MessageCompose;
    // Convert conversationId from EWS to REST format for Graph API compatibility
    context.conversationId = convertToRestId(message.conversationId) ?? null;
  }

  return context;
}

/**
 * Get the current user's email address from Office context
 */
export function getUserEmail(): string | null {
  if (!isOfficeInitialized()) {
    return null;
  }
  return Office.context?.mailbox?.userProfile?.emailAddress ?? null;
}

/**
 * Parse a message object to EmailMessage format
 */
export async function getCurrentEmailItem(): Promise<EmailItem | null> {
  if (!isOfficeInitialized()) {
    return null;
  }
  const item = Office.context.mailbox.item;
  if (!item || item.itemType !== Office.MailboxEnums.ItemType.Message) {
    return null;
  }

  const message = item as Office.MessageRead;
  const from = message.from;

  // Note: in Read mode, itemId is available. In Compose mode it might be undefined.
  if (!message.itemId) {
    return null;
  }

  // Convert EWS itemId to REST format for Graph API compatibility
  const restItemId = convertToRestId(message.itemId);

  return {
    from: from?.displayName ?? from?.emailAddress ?? 'Unknown',
    id: restItemId ?? message.itemId,
    sender: from?.displayName ?? from?.emailAddress ?? 'Unknown',
    senderColor: 'bg-info',
    senderInitial: (from?.displayName ?? 'U').charAt(0).toUpperCase(),
    subject: message.subject,
    time: message.dateTimeCreated
      ? message.dateTimeCreated.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '',
  };
}

/**
 * Display a specific email by its ID
 * @param itemId - The Outlook item ID to display
 */
export async function displayEmail(itemId: string): Promise<void> {
  if (!isOfficeInitialized()) {
    throw new Error('Office.js not initialized');
  }

  try {
    // Convert from REST (backend) to EWS (Office.js) if necessary
    // If it's already EWS, the conversion might fail or return same value depending on logic
    // but typically citations from backend are REST.
    const ewsId = convertToEwsId(itemId);
    Office.context.mailbox.displayMessageForm(ewsId ?? itemId);
  } catch (error) {
    console.error('Failed to display email:', error);
    throw error;
  }
}

/**
 * Capability probes for Outlook item mode.
 *
 * We detect mode by which APIs the current item exposes, not by the truthiness
 * of `itemId` / `conversationId`. Those IDs are not reliably populated
 * synchronously across every Outlook host (notably new Outlook on Mac,
 * delegate mailboxes, and some cached scenarios), which used to cause read-mode
 * items to be misclassified as compose and trigger
 * `body.setAsync is not a function`.
 */
function hasComposeBodyApi(): boolean {
  if (typeof Office === 'undefined') {
    return false;
  }
  const item = Office.context?.mailbox?.item;
  if (item === null || item === undefined) {
    return false;
  }
  return typeof (item as Office.MessageCompose).body?.setAsync === 'function';
}

function hasDisplayReplyForm(): boolean {
  if (typeof Office === 'undefined') {
    return false;
  }
  const item = Office.context?.mailbox?.item;
  if (item === null || item === undefined) {
    return false;
  }
  return typeof (item as Office.MessageRead).displayReplyForm === 'function';
}

/**
 * Viewing a sent/received message that supports Reply / Reply All.
 */
export function isInReadMode(): boolean {
  return hasDisplayReplyForm();
}

/**
 * Composing a brand-new message (no underlying saved conversation yet).
 */
export function isInComposeMode(): boolean {
  if (!hasComposeBodyApi()) {
    return false;
  }
  return !Office.context.mailbox.item?.conversationId;
}

/**
 * Editing an existing draft — compose APIs are available but the item is
 * already attached to a saved conversation.
 */
export function isReadingDraft(): boolean {
  if (!hasComposeBodyApi()) {
    return false;
  }
  return !!Office.context.mailbox.item?.conversationId;
}

/**
 * Check if Reply All is valid (multiple recipients exist)
 */
/**
 * Display reply form with pre-filled content. Treats `body` as HTML by
 * default (drafts produced by the assistant are HTML); pass `isHtml: false`
 * for plain-text/Markdown input to run it through the Markdown converter.
 */
export async function displayReplyWithContent(body: string, isHtml = true): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!Office.context?.mailbox?.item) {
      reject(new Error('No email is open'));
      return;
    }

    try {
      const htmlBody = isHtml ? body : convertMarkdownToHtml(body);

      Office.context.mailbox.item.displayReplyForm({
        htmlBody,
      });
      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Display reply all form with pre-filled content. Same conventions as
 * displayReplyWithContent.
 */
export async function displayReplyAllWithContent(body: string, isHtml = true): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!Office.context?.mailbox?.item) {
      reject(new Error('No email is open'));
      return;
    }

    try {
      const htmlBody = isHtml ? body : convertMarkdownToHtml(body);

      Office.context.mailbox.item.displayReplyAllForm({
        htmlBody,
      });
      resolve();
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Add a handler for when the selected item changes (e.g., switching emails while pinned)
 * @param callback - Function to call when the item changes
 */
export function addItemChangedHandler(
  callback: (itemId: string | null, conversationId: string | null) => void,
): void {
  if (!isOfficeInitialized()) {
    return;
  }

  // Check if the ItemChanged event is supported (requires Mailbox 1.5+)
  if (!Office.context.requirements.isSetSupported('Mailbox', '1.5')) {
    return;
  }

  try {
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      () => {
        try {
          const context = getOutlookContext();
          callback(context.itemId, context.conversationId);
        } catch (error) {
          console.error('Error processing ItemChanged event:', error);
        }
      },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error('Failed to add ItemChanged handler:', asyncResult.error);
        } else {
          console.log('Successfully registered ItemChanged handler');
        }
      },
    );
  } catch (error) {
    console.error('Error invoking addHandlerAsync:', error);
  }
}

export function selectedItemChangedHandler(
  callback: (itemId: string | null, conversationId: string | null) => void,
): void {
  if (!isOfficeInitialized()) {
    return;
  }

  // Check if the SelectedItemsChanged event is supported (requires Mailbox 1.13+)
  if (!Office.context.requirements.isSetSupported('Mailbox', '1.13')) {
    return;
  }

  try {
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.SelectedItemsChanged,
      () => {
        try {
          const context = getOutlookContext();
          callback(context.itemId, context.conversationId);
        } catch (error) {
          console.error('Error processing SelectedItemsChanged event:', error);
        }
      },
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error('Failed to add SelectedItemsChanged handler:', asyncResult.error);
        } else {
          console.log('Successfully registered SelectedItemsChanged handler');
        }
      },
    );
  } catch (error) {
    console.error('Error invoking addHandlerAsync:', error);
  }
}

/**
 * Remove the ItemChanged event handler
 */
export function removeItemChangedHandler(): void {
  if (!isOfficeInitialized()) {
    return;
  }

  // Additional check for mailbox availability
  if (!Office?.context?.mailbox) {
    return;
  }

  try {
    Office.context.mailbox.removeHandlerAsync(Office.EventType.ItemChanged, {}, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        console.error('Failed to remove ItemChanged handler:', asyncResult.error);
      }
    });
  } catch (error) {
    console.error('Error removing ItemChanged handler:', error);
  }
}

export function removeSelectedItemChangedHandler(): void {
  if (!isOfficeInitialized()) {
    return;
  }

  // Additional check for mailbox availability
  if (!Office?.context?.mailbox) {
    return;
  }

  try {
    Office.context.mailbox.removeHandlerAsync(
      Office.EventType.SelectedItemsChanged,
      {},
      (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          console.error('Failed to remove SelectedItemsChanged handler:', asyncResult.error);
        }
      },
    );
  } catch (error) {
    console.error('Error removing SelectedItemsChanged handler:', error);
  }
}
